import { disableCooperativeYielding as disableCooperativeYielding$1, disableInterruption as disableInterruption$1, disableOpSupervision as disableOpSupervision$1, disableRuntimeMetrics as disableRuntimeMetrics$1, disableWindDown as disableWindDown$1, enableCooperativeYielding as enableCooperativeYielding$1, enableInterruption as enableInterruption$1, enableOpSupervision as enableOpSupervision$1, enableRuntimeMetrics as enableRuntimeMetrics$1, enableWindDown as enableWindDown$1 } from '../../internal/layer/circular.esm.js';
import { None as None$1, Interruption as Interruption$1, OpSupervision as OpSupervision$1, RuntimeMetrics as RuntimeMetrics$1, WindDown as WindDown$1, CooperativeYielding as CooperativeYielding$1, cooperativeYielding as cooperativeYielding$1, diff as diff$1, differ as differ$1, disable as disable$1, disableAll as disableAll$1, enable as enable$1, enableAll as enableAll$1, interruptible as interruptible$1, interruption as interruption$1, isEnabled as isEnabled$1, isDisabled as isDisabled$1, make as make$1, none as none$1, opSupervision as opSupervision$1, patch as patch$1, render as render$1, runtimeMetrics as runtimeMetrics$1, toSet as toSet$1, windDown as windDown$1 } from '../../internal/runtimeFlags.esm.js';

/**
 * @since 2.0.0
 */

/**
 * Represents a set of `RuntimeFlag`s. `RuntimeFlag`s affect the operation of
 * the Effect runtime system. They are exposed to application-level code because
 * they affect the behavior and performance of application code.
 *
 * @since 2.0.0
 * @category models
 */

/**
 * Represents a flag that can be set to enable or disable a particular feature
 * of the Effect runtime.
 *
 * @since 2.0.0
 * @category models
 */

/**
 * No runtime flags.
 *
 * @since 2.0.0
 * @category constructors
 */
const None = None$1;

/**
 * The interruption flag determines whether or not the Effect runtime system will
 * interrupt a fiber.
 *
 * @since 2.0.0
 * @category constructors
 */
const Interruption = Interruption$1;

/**
 * The op supervision flag determines whether or not the Effect runtime system
 * will supervise all operations of the Effect runtime. Use of this flag will
 * negatively impact performance, but is required for some operations, such as
 * profiling.
 *
 * @since 2.0.0
 * @category constructors
 */
const OpSupervision = OpSupervision$1;

/**
 * The runtime metrics flag determines whether or not the Effect runtime system
 * will collect metrics about the Effect runtime. Use of this flag will have a
 * very small negative impact on performance, but generates very helpful
 * operational insight into running Effect applications that can be exported to
 * Prometheus or other tools via Effect Metrics.
 *
 * @since 2.0.0
 * @category constructors
 */
const RuntimeMetrics = RuntimeMetrics$1;

/**
 * The wind down flag determines whether the Effect runtime system will execute
 * effects in wind-down mode. In wind-down mode, even if interruption is
 * enabled and a fiber has been interrupted, the fiber will continue its
 * execution uninterrupted.
 *
 * @since 2.0.0
 * @category constructors
 */
const WindDown = WindDown$1;

/**
 * The cooperative yielding flag determines whether the Effect runtime will
 * yield to another fiber.
 *
 * @since 2.0.0
 * @category constructors
 */
const CooperativeYielding = CooperativeYielding$1;

/**
 * Returns `true` if the `CooperativeYielding` `RuntimeFlag` is enabled, `false`
 * otherwise.
 *
 * @since 2.0.0
 * @category getters
 */
const cooperativeYielding = cooperativeYielding$1;

/**
 * Creates a `RuntimeFlagsPatch` which describes the difference between `self`
 * and `that`.
 *
 * @since 2.0.0
 * @category diffing
 */
const diff = diff$1;

/**
 * Constructs a differ that knows how to diff `RuntimeFlags` values.
 *
 * @since 2.0.0
 * @category utils
 */
const differ = differ$1;

/**
 * Disables the specified `RuntimeFlag`.
 *
 * @since 2.0.0
 * @category utils
 */
const disable = disable$1;

/**
 * Disables all of the `RuntimeFlag`s in the specified set of `RuntimeFlags`.
 *
 * @since 2.0.0
 * @category utils
 */
const disableAll = disableAll$1;

/**
 * @since 2.0.0
 * @category context
 */
const disableCooperativeYielding = disableCooperativeYielding$1;

/**
 * @since 2.0.0
 * @category context
 */
const disableInterruption = disableInterruption$1;

/**
 * @since 2.0.0
 * @category context
 */
const disableOpSupervision = disableOpSupervision$1;

/**
 * @since 2.0.0
 * @category context
 */
const disableRuntimeMetrics = disableRuntimeMetrics$1;

/**
 * @since 2.0.0
 * @category context
 */
const disableWindDown = disableWindDown$1;

/**
 * Enables the specified `RuntimeFlag`.
 *
 * @since 2.0.0
 * @category utils
 */
const enable = enable$1;

/**
 * Enables all of the `RuntimeFlag`s in the specified set of `RuntimeFlags`.
 *
 * @since 2.0.0
 * @category utils
 */
const enableAll = enableAll$1;

/**
 * @since 2.0.0
 * @category context
 */
const enableCooperativeYielding = enableCooperativeYielding$1;

/**
 * @since 2.0.0
 * @category context
 */
const enableInterruption = enableInterruption$1;

/**
 * @since 2.0.0
 * @category context
 */
const enableOpSupervision = enableOpSupervision$1;

/**
 * @since 2.0.0
 * @category context
 */
const enableRuntimeMetrics = enableRuntimeMetrics$1;

/**
 * @since 2.0.0
 * @category context
 */
const enableWindDown = enableWindDown$1;

/**
 * Returns true only if the `Interruption` flag is **enabled** and the
 * `WindDown` flag is **disabled**.
 *
 * A fiber is said to be interruptible if interruption is enabled and the fiber
 * is not in its wind-down phase, in which it takes care of cleanup activities
 * related to fiber shutdown.
 *
 * @since 2.0.0
 * @category getters
 */
const interruptible = interruptible$1;

/**
 * Returns `true` if the `Interruption` `RuntimeFlag` is enabled, `false`
 * otherwise.
 *
 * @since 2.0.0
 * @category getters
 */
const interruption = interruption$1;

/**
 * Returns `true` if the specified `RuntimeFlag` is enabled, `false` otherwise.
 *
 * @since 2.0.0
 * @category elements
 */
const isEnabled = isEnabled$1;

/**
 * Returns `true` if the specified `RuntimeFlag` is disabled, `false` otherwise.
 *
 * @since 2.0.0
 * @category elements
 */
const isDisabled = isDisabled$1;

/**
 * @since 2.0.0
 * @category constructors
 */
const make = make$1;

/**
 * @since 2.0.0
 * @category constructors
 */
const none = none$1;

/**
 * Returns `true` if the `OpSupervision` `RuntimeFlag` is enabled, `false`
 * otherwise.
 *
 * @since 2.0.0
 * @category getters
 */
const opSupervision = opSupervision$1;

/**
 * Patches a set of `RuntimeFlag`s with a `RuntimeFlagsPatch`, returning the
 * patched set of `RuntimeFlag`s.
 *
 * @since 2.0.0
 * @category utils
 */
const patch = patch$1;

/**
 * Converts the provided `RuntimeFlags` into a `string`.
 *
 * @category conversions
 * @since 2.0.0
 */
const render = render$1;

/**
 * Returns `true` if the `RuntimeMetrics` `RuntimeFlag` is enabled, `false`
 * otherwise.
 *
 * @since 2.0.0
 * @category getters
 */
const runtimeMetrics = runtimeMetrics$1;

/**
 * Converts the provided `RuntimeFlags` into a `ReadonlySet<number>`.
 *
 * @category conversions
 * @since 2.0.0
 */
const toSet = toSet$1;

/**
 * Returns `true` if the `WindDown` `RuntimeFlag` is enabled, `false`
 * otherwise.
 *
 * @since 2.0.0
 * @category getters
 */
const windDown = windDown$1;

export { CooperativeYielding, Interruption, None, OpSupervision, RuntimeMetrics, WindDown, cooperativeYielding, diff, differ, disable, disableAll, disableCooperativeYielding, disableInterruption, disableOpSupervision, disableRuntimeMetrics, disableWindDown, enable, enableAll, enableCooperativeYielding, enableInterruption, enableOpSupervision, enableRuntimeMetrics, enableWindDown, interruptible, interruption, isDisabled, isEnabled, make, none, opSupervision, patch, render, runtimeMetrics, toSet, windDown };
